import { BaseClient } from '../client/BaseClient';
import { Contact, ContactsResponse } from '../types';

export class Contacts {
  constructor(private client: BaseClient) {}

  /**
   * List all contacts
   * @param tel - Phone number
   * @param search - Optional search query
   */
  async list(tel: string, search?: string): Promise<Contact[]> {
    const params = search ? { search } : undefined;
    const response = await this.client['get']<ContactsResponse>(
      `/api/contacts/${encodeURIComponent(tel)}`,
      { params }
    );
    
    return response.contacts;
  }

  /**
   * Get contact details
   * @param tel - Phone number
   * @param contactId - Contact ID
   */
  async get(tel: string, contactId: string): Promise<Contact> {
    const response = await this.client['get']<{
      success: boolean;
      contact: Contact;
    }>(`/api/contacts/${encodeURIComponent(tel)}/${encodeURIComponent(contactId)}`);
    
    return response.contact;
  }

  /**
   * Check if numbers are registered on WhatsApp
   * @param tel - Phone number
   * @param numbers - Array of phone numbers to check
   */
  async checkRegistered(
    tel: string,
    numbers: string[]
  ): Promise<{ [number: string]: boolean }> {
    const response = await this.client['post']<{
      success: boolean;
      results: { [number: string]: boolean };
    }>('/api/contacts/check', {
      tel,
      numbers,
    });
    
    return response.results;
  }

  /**
   * Get contact profile picture
   * @param tel - Phone number
   * @param contactId - Contact ID
   */
  async getProfilePicture(
    tel: string,
    contactId: string
  ): Promise<{ url: string }> {
    const response = await this.client['get']<{
      success: boolean;
      url: string;
    }>(`/api/contacts/${encodeURIComponent(tel)}/${encodeURIComponent(contactId)}/picture`);
    
    return { url: response.url };
  }

  /**
   * Get contact status/about
   * @param tel - Phone number
   * @param contactId - Contact ID
   */
  async getStatus(
    tel: string,
    contactId: string
  ): Promise<{ status: string }> {
    const response = await this.client['get']<{
      success: boolean;
      status: string;
    }>(`/api/contacts/${encodeURIComponent(tel)}/${encodeURIComponent(contactId)}/status`);
    
    return { status: response.status };
  }

  /**
   * Block a contact
   * @param tel - Phone number
   * @param contactId - Contact ID to block
   */
  async block(tel: string, contactId: string): Promise<{ success: boolean }> {
    return this.client['post']<{ success: boolean }>('/api/contacts/block', {
      tel,
      contactId,
    });
  }

  /**
   * Unblock a contact
   * @param tel - Phone number
   * @param contactId - Contact ID to unblock
   */
  async unblock(tel: string, contactId: string): Promise<{ success: boolean }> {
    return this.client['post']<{ success: boolean }>('/api/contacts/unblock', {
      tel,
      contactId,
    });
  }

  /**
   * Get blocked contacts
   * @param tel - Phone number
   */
  async getBlocked(tel: string): Promise<string[]> {
    const response = await this.client['get']<{
      success: boolean;
      blockedContacts: string[];
    }>(`/api/contacts/${encodeURIComponent(tel)}/blocked`);
    
    return response.blockedContacts;
  }

  /**
   * Get business profile of a contact
   * @param tel - Phone number
   * @param contactId - Business contact ID
   */
  async getBusinessProfile(
    tel: string,
    contactId: string
  ): Promise<any> {
    const response = await this.client['get']<{
      success: boolean;
      profile: any;
    }>(`/api/contacts/${encodeURIComponent(tel)}/${encodeURIComponent(contactId)}/business`);
    
    return response.profile;
  }
}